import React, { useState, useEffect } from 'react';
import { getPublicTiles } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { ExternalLink, LayoutGrid, Loader2 } from 'lucide-react';

const PortalPage = () => {
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTiles = async () => {
      try {
        const data = await getPublicTiles();
        setTiles(data);
      } catch (err) {
        setError('Fehler beim Laden der Kacheln');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTiles();
  }, []);

  const handleOpenTile = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-[#0E4C92] text-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-[#0E4C92] font-bold text-lg">GB</span>
              </div>
              <div>
                <h1 className="text-xl font-semibold tracking-tight">GB Sylt Portal</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Willkommen im Portal</h2>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#0E4C92] animate-spin" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-16">
              <p className="text-red-500">{error}</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && tiles.length === 0 && (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <LayoutGrid className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">Keine Tools verfügbar</h3>
              <p className="text-gray-500 mt-1">Es wurden noch keine Tools konfiguriert.</p>
            </div>
          )}

          {/* Tiles Grid */}
          {!loading && !error && tiles.length > 0 && (
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {tiles.map((tile) => (
                <Card 
                  key={tile.id} 
                  className="group hover:shadow-lg transition-shadow duration-200 border border-gray-200"
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 bg-[#0E4C92]/10 rounded-lg flex items-center justify-center group-hover:bg-[#0E4C92]/15 transition-colors">
                        <ExternalLink className="w-6 h-6 text-[#0E4C92]" />
                      </div>
                    </div>
                    <CardTitle className="text-lg mt-3">{tile.title}</CardTitle>
                    {tile.description && (
                      <CardDescription className="line-clamp-2">
                        {tile.description}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardFooter className="pt-0">
                    <Button 
                      onClick={() => handleOpenTile(tile.url)}
                      className="w-full bg-[#0E4C92] hover:bg-[#0a3a70]"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Öffnen
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500">
            © {new Date().getFullYear()} GB Sylt Portal. Alle Rechte vorbehalten.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PortalPage;
